import { DesignData, GroupElement, MediaElement, Page, Spread } from '../types';

// Artwork can only move between spreads that are the same kind of surface. What
// makes them the same is described here as a value, so comparing two surfaces is
// comparing their descriptions.
//
// The pages are described by size and by every group except the artwork itself:
// the frames the artwork sits in, and everything that constrains where it may
// sit — bleed, media box, die cut, fold lines, punch holes. Geometry is included,
// not just presence: two pages punched or cut in different places are different
// surfaces, and element coordinates are page-relative, so artwork composed for
// one would land wrongly on the other.
//
// The spread is described by the properties that stay behind when artwork moves
// and change how it is printed. Artwork must not cross between surfaces that
// disagree on those.
interface SurfaceDescription {
  pages: Array<{
    width: number;
    height: number;
    groups: Array<[string, Array<Record<string, unknown>>]>;
  }>;
  technology: string | null;
  spotFinishing: string | null;
  whiteUnderbase: boolean;
  tags: string[];
}

// Only the fields that place an element on the surface. What the catalogue calls
// a frame is left out: labels describe the slot, they stay on it when artwork
// moves, and two surfaces that agree on every position but not on their wording
// still hold each other's artwork correctly.
function describeGroupElement(element: GroupElement): Record<string, unknown> {
  const { type, x, y, width, height } = element;
  const description: Record<string, unknown> = { type, x, y, width, height };

  const { path } = element as GroupElement & { path?: string };
  if (path !== undefined) {
    description.path = path;
  }

  return description;
}

function describeSurface(spread: Spread): SurfaceDescription {
  return {
    pages: spread.pages.map((page) => ({
      width: page.width,
      height: page.height,
      groups: (Object.keys(page.groups) as Array<keyof Page['groups']>)
        .filter((group) => group !== 'media')
        .sort()
        .map((group) => [group, ((page.groups[group] ?? []) as GroupElement[]).map(describeGroupElement)]),
    })),
    technology: spread.technology ?? null,
    spotFinishing: spread.spot_finishing_type ?? null,
    whiteUnderbase: !!spread.white_underbase,
    tags: [...(spread.tags ?? [])].sort(),
  };
}

// Cached on the spread itself: the description depends on nothing else, and the
// store replaces a spread object whenever its contents change.
const shapeKeys = new WeakMap<Spread, string>();

function getSpreadShapeKey(spread: Spread): string {
  const cached = shapeKeys.get(spread);
  if (cached !== undefined) {
    return cached;
  }

  const key = JSON.stringify(describeSurface(spread));
  shapeKeys.set(spread, key);
  return key;
}

// Some artwork is derived from the slot it sits on rather than authored by the
// user, and nothing re-derives it after a move. A spread holding any takes no
// part in a reorder.
function hasSlotDerivedContent(spread: Spread): boolean {
  return spread.pages.some((page) =>
    (page.groups.media ?? []).some(
      (element: MediaElement) =>
        element.type === 'grid' || (element as { generatedType?: string }).generatedType !== undefined,
    ),
  );
}

// Groups the given spread indexes by surface, keeping the order they were passed
// in. Callers pass the indexes a user can actually act on, so spreads hidden
// from them never take part.
function groupSpreadIndexesByShape(design: DesignData, eligibleSpreadIndexes: number[]): Map<string, number[]> {
  const groups = new Map<string, number[]>();

  eligibleSpreadIndexes.forEach((spreadIndex) => {
    const spread = design.spreads[spreadIndex];
    if (!spread || hasSlotDerivedContent(spread)) {
      return;
    }

    const shapeKey = getSpreadShapeKey(spread);
    const indexes = groups.get(shapeKey);
    if (indexes) {
      indexes.push(spreadIndex);
    } else {
      groups.set(shapeKey, [spreadIndex]);
    }
  });

  return groups;
}

// The spreads whose artwork can be moved: those with at least one peer to
// exchange with.
function getReorderableSpreadIndexes(design: DesignData, eligibleSpreadIndexes: number[]): Set<number> {
  const reorderable = new Set<number>();

  groupSpreadIndexesByShape(design, eligibleSpreadIndexes).forEach((indexes) => {
    if (indexes.length > 1) {
      indexes.forEach((spreadIndex) => reorderable.add(spreadIndex));
    }
  });

  return reorderable;
}

// The slots the artwork at `spreadIndex` can move to, in the order the caller
// presented them. Empty when it has no peer, or when its own artwork is derived
// from its slot and so cannot move at all.
function getInterchangeableSpreadIndexes(
  design: DesignData,
  spreadIndex: number,
  eligibleSpreadIndexes: number[],
): number[] {
  const spread = design.spreads[spreadIndex];
  if (!spread || hasSlotDerivedContent(spread)) {
    return [];
  }

  const indexes = groupSpreadIndexesByShape(design, eligibleSpreadIndexes).get(getSpreadShapeKey(spread)) ?? [];

  return indexes.length > 1 ? indexes : [];
}

export { getSpreadShapeKey, hasSlotDerivedContent, getReorderableSpreadIndexes, getInterchangeableSpreadIndexes };
